import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Project, ProjectMember } from '../interfaces/project';
import { useLocation } from 'react-router-dom';

interface UserPermissions {
  isAdmin: boolean;
  isObserver: boolean;
  canEdit: boolean;
  canInvite: boolean;
  canDelete: boolean;
}

/**
 * Hook para verificar as permissões do usuário em relação a um projeto
 * 
 * @param project O projeto atual
 * @returns Objeto com as permissões do usuário
 */
export const useUserPermissions = (project: Project | null): UserPermissions => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Verificar se há flag de override administrativo
  const adminOverride = location.state?.adminOverride;
  const [permissions, setPermissions] = useState<UserPermissions>({
    isAdmin: false,
    isObserver: false,
    canEdit: false,
    canInvite: false,
    canDelete: false,
  });

  useEffect(() => {
    if (!user || !user.id || !project) {
      // Reset permissions when no user or project
      setPermissions({
        isAdmin: false,
        isObserver: false,
        canEdit: false,
        canInvite: false,
        canDelete: false,
      });
      return;
    }

    const userId = user.id;
    
    // Verificar se o usuário é observador global
    const isGlobalObserver = !!user.isObserver;
    
    // Verificar se o usuário é observador específico do projeto
    const isProjectObserver = project.observers?.some(
      (observer: ProjectMember) => observer.id === userId
    );
    
    // Verificar se o usuário é observador (global ou do projeto)
    const isObserver = isGlobalObserver || !!isProjectObserver;

    // Verificar se o usuário é o criador ou proprietário
    const isOwner = project.ownerId === userId || project.creatorId === userId;
    
    // Verificar nas listas de administradores do projeto
    const isProjectAdmin = 
      project.admins?.some((admin: ProjectMember) => admin.id === userId) || 
      project.administrators?.some((admin: ProjectMember) => admin.id === userId);
    
    // Verificar se o usuário é administrador global
    const isGlobalAdmin = (
      user.role?.toLowerCase() === 'admin' || 
      user.role?.toLowerCase() === 'administrator' || 
      user.role?.toLowerCase() === 'developer' || 
      user.role?.toLowerCase() === 'dev' ||
      // Verificar se o título inclui "Administrador do Sistema"
      (typeof user.role === 'string' && user.role.toLowerCase().includes('administrador do sistema'))
    );
    
    // Verificar se o projeto indica que o usuário atual é administrador do sistema
    const isSystemAdminAccordingToProject = project.currentUserIsSystemAdmin === true;
    
    // Considerar o override administrativo se existir
    const hasAdminOverride = (adminOverride === true) && (
      user.role?.toLowerCase() === 'admin' || 
      user.role?.toLowerCase() === 'administrator' || 
      user.role?.toLowerCase() === 'developer' || 
      user.role?.toLowerCase() === 'dev' ||
      // Verificar se o título inclui "Administrador do Sistema"
      (typeof user.role === 'string' && user.role.toLowerCase().includes('administrador do sistema'))
    );
    
    // Combinação final de permissões administrativas
    const isAdmin = isProjectAdmin || isGlobalAdmin || isOwner || hasAdminOverride || isSystemAdminAccordingToProject;
    
    // Administradores do sistema sempre podem editar, independente de serem observadores
    const canSystemAdminEdit = isGlobalAdmin || isSystemAdminAccordingToProject;
    
    setPermissions({
      isAdmin,
      isObserver,
      canEdit: canSystemAdminEdit || (isAdmin && !isObserver), // System admins sempre podem editar, outros admins podem editar exceto se for observador
      canInvite: canSystemAdminEdit || (isAdmin && !isObserver), // System admins sempre podem convidar
      canDelete: canSystemAdminEdit || (isAdmin && !isObserver), // System admins sempre podem excluir
    });
  }, [user, project]);

  return permissions;
};
